
import React, { useState, useEffect } from 'react';
import { getArticles, deleteArticle, resetArticles, Article } from '../../../utils/articleApi';
import { formatArticleCategoriesLine } from '../../../utils/articleCategories';

interface ArticleListProps {
  onEdit: (article: Article) => void;
  onCreateNew: () => void;
}

export default function ArticleList({ onEdit, onCreateNew }: ArticleListProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filter, setFilter] = useState<'all' | '公開' | '下書き' | '非公開'>('all');
  const [loading, setLoading] = useState(true);

  // 記事を読み込む
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    const data = await getArticles();
    console.log('ArticleList: Loaded articles:', data.length);
    setArticles(data);
    setLoading(false);
  };

  const handleReset = async () => {
    if (window.confirm('ローカルストレージをクリアして、JSONファイルから記事を再読み込みしますか？')) {
      await resetArticles();
      await loadArticles();
      alert('記事データをリセットしました。');
    }
  };

  const filteredArticles = filter === 'all'
    ? articles
    : articles.filter(article => article.status === filter);

  const handleDelete = async (id: number | string) => {
    if (window.confirm('この記事を削除してもよろしいですか？')) {
      const success = await deleteArticle(id);
      if (success) {
        // 記事リストを再読み込み
        await loadArticles();
        alert('記事を削除しました。');
      } else {
        alert('記事の削除に失敗しました。');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '公開':
        return 'bg-green-100 text-green-800';
      case '下書き':
        return 'bg-yellow-100 text-yellow-800';
      case '非公開':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <i className="ri-loader-4-line text-2xl text-gray-400"></i>
        </div>
        <p className="text-gray-600">記事を読み込んでいます...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-[#E5E7EB]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#111827]">記事一覧</h2>
            <p className="text-[#6B7280] text-sm">全{filteredArticles.length}件の記事</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent pr-8"
            >
              <option value="all">すべて</option>
              <option value="公開">公開</option>
              <option value="下書き">下書き</option>
              <option value="非公開">非公開</option>
            </select>

            {/* Reset Button */}
            <button
              onClick={handleReset}
              className="border border-[#D1D5DB] text-[#6B7280] px-4 py-2 rounded-lg hover:bg-[#F9FAFB] transition-colors flex items-center space-x-2 whitespace-nowrap"
              title="ローカルストレージをクリア"
            >
              <i className="ri-refresh-line"></i>
              <span>リセット</span>
            </button>

            {/* Create Button */}
            <button
              onClick={onCreateNew}
              className="bg-[#14B8A6] text-white px-4 py-2 rounded-lg hover:bg-[#0F9488] transition-colors flex items-center space-x-2 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              <span>新規作成</span>
            </button>
          </div>
        </div>
      </div>

      {/* Article List */}
      <div className="divide-y divide-[#E5E7EB]">
        {filteredArticles.map((article) => (
          <div key={article.id} className="p-6 hover:bg-[#F9FAFB] transition-colors">
            <div className="flex items-start space-x-4">
              {/* Thumbnail */}
              <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#111827] truncate">
                        {article.title}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(article.status)}`}>
                        {article.status}
                      </span>
                    </div>
                    
                    <p className="text-[#6B7280] text-sm mb-3 line-clamp-2">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-[#9CA3AF]">
                      <span className="flex items-center space-x-1">
                        <i className="ri-user-line"></i>
                        <span>{article.author}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <i className="ri-calendar-line"></i>
                        <span>{article.publishDate}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <i className="ri-price-tag-3-line"></i>
                        <span>{formatArticleCategoriesLine(article)}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <i className="ri-time-line"></i>
                        <span>{article.readTime}</span>
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => onEdit(article)}
                      className="p-2 text-[#6B7280] hover:text-[#14B8A6] hover:bg-[#F0FDFA] rounded-lg transition-colors"
                      title="編集"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className="p-2 text-[#6B7280] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="削除"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredArticles.length === 0 && (
        <div className="p-12 text-center">
          <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-article-line text-2xl text-[#9CA3AF]"></i>
          </div>
          <h3 className="text-lg font-medium text-[#111827] mb-2">記事がありません</h3>
          <p className="text-[#6B7280] mb-6">新しい記事を作成してみましょう</p>
          <button
            onClick={onCreateNew}
            className="bg-[#14B8A6] text-white px-6 py-2 rounded-lg hover:bg-[#0F9488] transition-colors"
          >
            記事を作成
          </button>
        </div>
      )}
    </div>
  );
}
